import React from "react";
import { useNavigate } from "react-router-dom";
import { Lightbulb, Ban, PieChart, Sparkles, ChevronDown, ChevronRight } from "lucide-react";
import { PrivateShareBooker } from "@/components/simple-ledger/PrivateShareBooker";

const chf = (v) => `CHF ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const KIND = {
  missing: { icon: Lightbulb, label: "Fehlender Abzug", cls: "bg-sky-50 border-sky-200 text-sky-900", badge: "bg-sky-100 text-sky-800" },
  non_deductible: { icon: Ban, label: "Nicht abzugsfähig", cls: "bg-rose-50 border-rose-200 text-rose-900", badge: "bg-rose-100 text-rose-800" },
  private_share: { icon: PieChart, label: "Privatanteil", cls: "bg-amber-50 border-amber-200 text-amber-900", badge: "bg-amber-100 text-amber-800" },
};

/** Zähler-Badge in der Monatszeile – Klick klappt die Tipps auf. */
export function TaxTipToggle({ tips, open, onToggle, month }) {
  if (!tips?.length) return <span className="text-[11px] text-slate-300">–</span>;
  const warn = tips.some((t) => t.severity === "warn");
  return (
    <button type="button" onClick={onToggle} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors ${warn ? "bg-amber-100 text-amber-800 hover:bg-amber-200" : "bg-sky-100 text-sky-800 hover:bg-sky-200"}`} data-testid={`tax-tip-toggle-${month}`}>
      <Lightbulb className="h-3 w-3" />{tips.length} {tips.length === 1 ? "Tipp" : "Tipps"}
      {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
    </button>
  );
}

/** Aufgeklappte Tipp-Liste unter einer Monatszeile. */
export function TaxTipRows({ tips, month, companyId, onBooked }) {
  const navigate = useNavigate();
  return (
    <div className="grid gap-2 py-2 pl-2 md:grid-cols-2" data-testid={`tax-tips-${month}`}>
      {tips.map((t) => {
        const k = KIND[t.kind] || KIND.missing;
        const Icon = k.icon;
        return (
          <div key={t.id} className={`rounded-lg border p-3 text-xs ${k.cls}`} data-testid={`tax-tip-${t.id}`}>
            <div className="flex items-start gap-2">
              <Icon className="h-4 w-4 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-semibold">{t.title}</span>
                  <span className={`rounded px-1.5 py-px text-[10px] ${k.badge}`}>{k.label}</span>
                  {t.amount != null && <span className="text-[10px] opacity-70 tabular-nums">{chf(t.amount)}</span>}
                </div>
                <p className="mt-1 leading-relaxed">{t.text}</p>
                {t.ai_text && (
                  <p className="mt-1.5 flex gap-1.5 rounded bg-white/70 px-2 py-1.5 leading-relaxed text-slate-700" data-testid={`tax-tip-ai-${t.id}`}>
                    <Sparkles className="h-3 w-3 shrink-0 mt-0.5 text-violet-500" /><span>{t.ai_text}</span>
                  </p>
                )}
                {t.entries?.length > 0 && (
                  <ul className="mt-1.5 space-y-0.5 text-[11px] opacity-80">
                    {t.entries.map((e) => <li key={e.id} className="truncate">· {e.date?.slice(8, 10)}.{e.date?.slice(5, 7)}. {e.description || "(ohne Text)"} <span className="opacity-60">Konto {e.account}</span></li>)}
                  </ul>
                )}
                {t.private_share && companyId && <PrivateShareBooker companyId={companyId} tip={t} onBooked={onBooked} />}
                {t.action && (
                  <button type="button" onClick={() => navigate(t.action.to)} className="mt-2 rounded-full bg-slate-900 px-3 py-1 text-[11px] font-medium text-white hover:bg-slate-700 transition-colors" data-testid={`tax-tip-action-${t.id}`}>
                    {t.action.label} →
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
